// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for viewing Maryland bill metadata details.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import type { MarylandBillMetadata } from '../../services/maryland-bills.service'

interface BillDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bill: MarylandBillMetadata | null
  testId?: string
}

export const BillDetailsDialog: FC<BillDetailsDialogProps> = ({ open, onOpenChange, bill, testId }) => {
  if (!bill) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle>{bill.BillNumber} — {bill.Title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-y-3 gap-x-4">
            <span className="font-medium text-muted-foreground">Status</span>
            <span>{bill.Status ?? '—'}</span>

            <span className="font-medium text-muted-foreground">Type</span>
            <span>{bill.BillType ?? '—'}</span>

            <span className="font-medium text-muted-foreground">Version</span>
            <span>{bill.BillVersion ?? '—'}</span>

            <span className="font-medium text-muted-foreground">Crossfile</span>
            <span>{bill.CrossfileBillNumber ?? '—'}</span>
          </div>

          {bill.Synopsis && (
            <div>
              <span className="font-medium text-muted-foreground">Synopsis</span>
              <p className="mt-1 text-muted-foreground">{bill.Synopsis}</p>
            </div>
          )}

          {bill.BroadSubjects && bill.BroadSubjects.length > 0 && (
            <div>
              <span className="font-medium text-muted-foreground">Broad Subjects</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {bill.BroadSubjects.map((s) => (
                  <Badge key={s.Code} variant="secondary" className="text-xs">{s.Name}</Badge>
                ))}
              </div>
            </div>
          )}

          {bill.NarrowSubjects && bill.NarrowSubjects.length > 0 && (
            <div>
              <span className="font-medium text-muted-foreground">Narrow Subjects</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {bill.NarrowSubjects.map((s) => (
                  <Badge key={s.Code} variant="outline" className="text-xs">{s.Name}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
